import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * HImageOverlay - A premium, animated image overlay/lightbox component.
 *
 * @param {boolean} isOpen - Whether the overlay is visible
 * @param {() => void} onClose - Function to call when closing the overlay
 * @param {string[]} images - Array of image URLs to display
 * @param {number} currentIndex - Index of the currently displayed image
 * @param {(index: number) => void} onIndexChange - Function to call when the image index changes
 */
const HImageOverlay = ({ isOpen, onClose, images, currentIndex, onIndexChange }) => {
  const handlePrev = useCallback(
    e => {
      e?.stopPropagation();
      const newIndex = (currentIndex - 1 + images.length) % images.length;
      onIndexChange(newIndex);
    },
    [currentIndex, images.length, onIndexChange]
  );

  const handleNext = useCallback(
    e => {
      e?.stopPropagation();
      const newIndex = (currentIndex + 1) % images.length;
      onIndexChange(newIndex);
    },
    [currentIndex, images.length, onIndexChange]
  );

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, handlePrev, handleNext]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-md px-4 select-none"
          onClick={onClose}
        >
          {/* Close button */}
          <button
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors z-[10000]"
            aria-label="Close overlay"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-8 left-1/2 -translate-x-1/2 text-white/90 font-medium z-[10000] bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Navigation - Prev */}
          {images.length > 1 && (
            <button
              onClick={handlePrev}
              className="absolute left-4 lg:left-8 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-[10000]"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          {/* Main Image Container */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="relative max-w-5xl w-full flex items-center justify-center pointer-events-none"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-full h-[70vh] flex items-center justify-center overflow-hidden rounded-xl shadow-2xl bg-black/10 border border-white/10 pointer-events-auto">
              <img
                src={images[currentIndex]}
                alt={`View ${currentIndex + 1}`}
                className="w-full h-full object-contain pointer-events-auto cursor-default"
              />
            </div>
          </motion.div>

          {/* Navigation - Next */}
          {images.length > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 lg:right-8 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-[10000]"
              aria-label="Next image"
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          {/* Footer UI - Helpful shortcut hint */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-xs hidden md:block">
            Use arrow keys or scroll buttons to navigate • ESC to close
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(overlayContent, document.body);
};

export default HImageOverlay;
